<template>
  <div class="content-wrapper view-aptlist-manage p-0">
    <div class="content df-correction-page">
      <!-- Page header -->
      <div class="page-head">
        <div>
          <h1>
            <i class="fas fa-edit title-fa"></i>
            <span>แก้ไข DF</span>
          </h1>
        </div>
        <div class="head-meta" v-if="lastUpdated">
          <span class="pill">
            <i class="fas fa-clock"></i> อัปเดตล่าสุด {{ lastUpdated }}
          </span>
        </div>
      </div>

      <div class="card df-card">
        <!-- Filter bar -->
        <div class="filter-bar">
          <div class="filter-grid">
            <!-- Date range -->
            <div class="field">
              <label><i class="fas fa-calendar"></i> ช่วงวันที่</label>
              <div
                class="date-range-wrap"
                :class="{ 'is-invalid': !dateRange[0] || !dateRange[1] }"
              >
                <date-picker
                  v-model="dateRange"
                  type="date"
                  range
                  format="DD MMM YY"
                  :shortcuts="dateRangeShortcuts"
                  :disabled-date="notAfterToday"
                  placeholder="เลือกวันที่ต้องการค้นหา"
                  input-class="df-date-input"
                  :clearable="false"
                ></date-picker>
                <span class="date-quick" @click="setToday">วันนี้</span>
              </div>
            </div>

            <!-- Doctor -->
            <div class="field">
              <label><i class="fas fa-user-md"></i> แพทย์</label>
              <div class="ctrl-wrap select-wrap">
                <select class="ctrl" v-model="selectedDoctor">
                  <option :value="null">ทุกแพทย์</option>
                  <option
                    v-for="d in doctorsList"
                    :key="d.uid"
                    :value="d.fullName"
                  >
                    {{ d.fullName }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Search -->
            <div class="field">
              <label><i class="fas fa-search"></i> ค้นหา</label>
              <div class="ctrl-wrap">
                <i class="fas fa-search lead"></i>
                <input
                  class="ctrl"
                  type="search"
                  v-model.trim="searchTable"
                  placeholder="ชื่อคนไข้, DN, เลขที่ใบเสร็จ หรือรายการรักษา"
                  data-testid="df-correction-search-input"
                />
                <i
                  v-if="searchTable"
                  class="fas fa-times-circle trail clearable"
                  @click="searchTable = ''"
                ></i>
              </div>
            </div>

            <!-- Actions -->
            <div class="field actions">
              <button
                class="btn btn-secondary"
                title="ล้างตัวกรอง"
                @click="resetFilters"
              >
                <i class="fas fa-undo"></i> ล้าง
              </button>
              <button class="btn btn-primary" @click="fetchReport">
                <i class="fas fa-search"></i> แสดงข้อมูล
              </button>
            </div>
          </div>
        </div>

        <!-- Quick view tabs + totals -->
        <div v-if="receiptItemList.length > 0" class="quick-row">
          <span class="label-text">มุมมอง:</span>
          <span
            v-for="tab in viewTabs"
            :key="tab.id"
            :class="['chip', { active: view === tab.id }]"
            @click="setView(tab.id)"
          >
            <span
              v-if="tab.dot"
              class="badge-dot"
              :style="{ background: tab.dot }"
            ></span>
            {{ tab.label }}
            <span class="chip-count">· {{ tab.count }}</span>
          </span>
          <div class="chip-counts">
            <span
              >รวมชำระ <strong>฿{{ formatMoney(totals.paid) }}</strong></span
            >
            <span
              >รวม DF <strong>฿{{ formatMoney(totals.df) }}</strong></span
            >
            <span
              >รวม CF <strong>฿{{ formatMoney(totals.cf) }}</strong></span
            >
          </div>
        </div>

        <!-- Table toolbar -->
        <div v-if="receiptItemList.length > 0" class="table-toolbar">
          <div class="left">
            <span
              >พบ <strong>{{ filteredItems.length }}</strong> รายการ</span
            >
            <span v-if="filteredItems.length > 0"
              >· แสดง
              <strong>{{ Math.min(perPage, filteredItems.length) }}</strong>
              ต่อหน้า</span
            >
          </div>
        </div>

        <!-- Table -->
        <div v-if="filteredItems.length > 0" class="tbl-wrap">
          <b-table
            ref="dfCorrectionTable"
            class="df-tbl my-0"
            small
            sort-icon-left
            head-variant="light"
            :busy="tableBusy"
            :items="filteredItems"
            :fields="fields"
            sort-by="creationDt"
            :sort-desc="true"
            :responsive="true"
            sticky-header="calc(100vh - 360px)"
            :per-page="perPage"
            :current-page="currentPage"
          >
            <template #cell(index)="row">{{
              (currentPage - 1) * perPage + row.index + 1
            }}</template>
            <template #cell(creationDt)="row">
              {{ formatDate(row.value) }}
            </template>
            <template #cell(receiptNo)="row">
              <span class="mono">{{ row.value }}</span>
            </template>
            <template #cell(dn)="row">
              <span class="mono">{{ row.value }}</span>
            </template>
            <template #cell(patientFullName)="row">
              <span class="name">{{ row.value }}</span>
            </template>
            <template #cell(doctorFullName)="row">
              <div class="doctor-cell">
                <div class="avatar">{{ doctorInitials(row.value) }}</div>
                <span>{{ row.value }}</span>
              </div>
            </template>
            <template #cell(paid)="row">
              <span class="num">{{ formatMoney(row.value) }}</span>
            </template>
            <template #cell(paidDf)="row">
              <span class="num df-num">{{ formatMoney(row.value) }}</span>
            </template>
            <template #cell(paidCf)="row">
              <span class="num">{{ formatMoney(row.value) }}</span>
            </template>
            <template #cell(dfStatus)="row">
              <span
                v-if="row.item.hasDfCorrection"
                class="badge-pill badge-warn"
                @click="openHistoryModal(row.item)"
              >
                <span class="badge-dot"></span> แก้ไขแล้ว
              </span>
              <span v-else class="badge-pill badge-ok">
                <span class="badge-dot"></span> ปกติ
              </span>
            </template>
            <template #cell(action)="row">
              <button
                class="btn btn-xs btn-outline"
                :disabled="isPartialPaid(row.item)"
                :title="
                  isPartialPaid(row.item)
                    ? 'รายการนี้จ่ายเพียงบางส่วน — ไม่สามารถแก้ DF ได้'
                    : ''
                "
                @click="openCorrectionModal(row.item)"
              >
                <i class="fas fa-edit"></i> แก้ไข DF
              </button>
              <button
                v-if="row.item.hasDfCorrection"
                class="btn btn-xs btn-outline-info btn-icon"
                title="ประวัติ"
                @click="openHistoryModal(row.item)"
              >
                <i class="fas fa-history"></i>
              </button>
            </template>
          </b-table>
        </div>

        <!-- Empty state -->
        <div v-else class="empty">
          <div class="ic"><i class="fas fa-inbox"></i></div>
          <h4>ไม่มีข้อมูลให้แสดง</h4>
          <p>
            <template v-if="receiptItemList.length === 0"
              >เลือกช่วงวันที่และกด "แสดงข้อมูล" เพื่อโหลดรายการ</template
            >
            <template v-else
              >ลองเปลี่ยนช่วงวันที่ ล้างตัวกรอง หรือเลือกมุมมองอื่น</template
            >
          </p>
        </div>

        <!-- Footer / pager -->
        <div v-if="filteredItems.length > 0" class="tbl-footer">
          <div>หน้า {{ currentPage }} จาก {{ totalPages }}</div>
          <b-pagination
            v-model="currentPage"
            :total-rows="filteredItems.length"
            :per-page="perPage"
            size="sm"
            first-number
            last-number
            pills
            limit="10"
            class="df-pager m-0"
          ></b-pagination>
        </div>
      </div>

      <div class="info-strip">
        <i class="fas fa-info-circle"></i>
        <span
          >การแก้ไข DF จะถูกบันทึกเป็นประวัติพร้อมเหตุผล
          และไม่กระทบต่อยอดใบเสร็จที่ออกไปแล้ว</span
        >
      </div>

      <DfCorrectionModal
        ref="dfCorrectionModal"
        @correctionSaved="onCorrectionSaved"
        @error="onError"
      />
      <DfCorrectionHistoryModal ref="dfCorrectionHistoryModal" />
      <Dialog ref="Dialog"></Dialog>
      <Loading v-if="isLoading"></Loading>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import DfCorrectionModal from "@/components/modal/DfCorrectionModal";
import DfCorrectionHistoryModal from "@/components/modal/DfCorrectionHistoryModal";
import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import moment from "moment";

export default {
  name: "DFCorrection",
  components: {
    DfCorrectionModal,
    DfCorrectionHistoryModal,
    Dialog,
    Loading,
  },
  data() {
    return {
      isLoading: false,
      tableBusy: false,
      selectedDoctor: null,
      dateRange: [],
      receiptItemList: [],
      searchTable: "",
      view: "all",
      lastUpdated: null,
      currentPage: 1,
      perPage: 100,
      fields: [
        { key: "index", label: "#", class: "col-idx" },
        {
          key: "creationDt",
          label: "วันที่",
          sortable: true,
          class: "narrow-spacing",
        },
        { key: "receiptNo", label: "เลขที่ใบเสร็จ", class: "narrow-spacing" },
        { key: "dn", label: "DN.", class: "narrow-spacing" },
        { key: "patientFullName", label: "ชื่อคนไข้", class: "narrow-spacing" },
        { key: "itemName", label: "รายการรักษา", class: "narrow-spacing" },
        { key: "doctorFullName", label: "แพทย์", class: "narrow-spacing" },
        { key: "paid", label: "ชำระ", class: "text-right" },
        { key: "paidDf", label: "DF", class: "text-right" },
        { key: "paidCf", label: "CF", class: "text-right" },
        { key: "dfStatus", label: "สถานะ" },
        { key: "action", label: "", class: "text-center" },
      ],
    };
  },
  created() {
    this.dateRange = [this.today, this.today];
  },
  computed: {
    ...mapGetters({
      doctorsList: "moduleUser/getDoctorList",
      isAccessMenu: "moduleUser/getIsAccessMenu",
      userInfo: "moduleUser/getUserInfo",
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
    }),
    doctorFiltered() {
      if (!this.selectedDoctor) return this.receiptItemList;
      return this.receiptItemList.filter(
        (r) => r.doctorFullName === this.selectedDoctor
      );
    },
    filteredItems() {
      const q = (this.searchTable || "").toLowerCase();
      return this.doctorFiltered.filter((item) => {
        if (this.view === "normal" && item.hasDfCorrection) return false;
        if (this.view === "corrected" && !item.hasDfCorrection) return false;
        if (!q) return true;
        return (
          (item.patientFullName || "").toLowerCase().includes(q) ||
          (item.dn || "").toString().toLowerCase().includes(q) ||
          (item.receiptNo || "").toString().toLowerCase().includes(q) ||
          (item.itemName || "").toLowerCase().includes(q)
        );
      });
    },
    counts() {
      return {
        all: this.doctorFiltered.length,
        normal: this.doctorFiltered.filter((r) => !r.hasDfCorrection).length,
        corrected: this.doctorFiltered.filter((r) => r.hasDfCorrection).length,
      };
    },
    viewTabs() {
      return [
        { id: "all", label: "ทั้งหมด", count: this.counts.all },
        { id: "normal", label: "ปกติ", count: this.counts.normal },
        {
          id: "corrected",
          label: "แก้ไขแล้ว",
          count: this.counts.corrected,
          dot: "#d97706",
        },
      ];
    },
    totals() {
      return this.filteredItems.reduce(
        (acc, r) => {
          acc.paid += Number(r.paid) || 0;
          acc.df += Number(r.paidDf) || 0;
          acc.cf += Number(r.paidCf) || 0;
          return acc;
        },
        { paid: 0, df: 0, cf: 0 }
      );
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredItems.length / this.perPage));
    },
    today() {
      let today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
    dateFrom() {
      return this.dateRange[0]
        ? moment(this.dateRange[0]).startOf("day").format()
        : "";
    },
    dateTo() {
      return this.dateRange[1]
        ? moment(this.dateRange[1]).endOf("day").format()
        : "";
    },
  },
  watch: {
    searchTable() {
      this.currentPage = 1;
    },
    view() {
      this.currentPage = 1;
    },
    selectedDoctor() {
      this.currentPage = 1;
    },
  },
  methods: {
    ...mapActions({
      fetchReceiptItems: "moduleDfCorrection/fetchReceiptItems",
    }),
    formatDate(date) {
      return date ? moment(date).locale("th").format("D MMM YY") : "";
    },
    formatMoney(num) {
      return Number(num || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    notAfterToday(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },
    setToday() {
      this.dateRange = [this.today, this.today];
    },
    setView(id) {
      this.view = id;
    },
    resetFilters() {
      this.selectedDoctor = null;
      this.searchTable = "";
      this.view = "all";
    },
    isPartialPaid(item) {
      const paid = Number(item.paid) || 0;
      const amount = Number(item.amount) || 0;
      return paid > 0 && Math.abs(paid - amount) >= 0.01;
    },
    doctorInitials(name) {
      if (!name) return "?";
      const cleaned = name
        .replace(/^(ทพ\.|ทพญ\.|นพ\.|พญ\.|ดร\.|Dr\.?)\s*/i, "")
        .trim();
      const parts = cleaned.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return parts[0].charAt(0) + parts[parts.length - 1].charAt(0);
      }
      if (parts.length === 1) return parts[0].substring(0, 2);
      return "?";
    },
    async fetchReport() {
      if (!this.dateRange[0] || !this.dateRange[1]) return;
      this.tableBusy = true;
      this.isLoading = true;
      try {
        const res = await this.fetchReceiptItems({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          startDt: this.dateFrom,
          endDt: this.dateTo,
        });
        this.receiptItemList = res.data.report || res.data || [];
        this.currentPage = 1;
        this.lastUpdated = moment().format("HH:mm");
      } catch (err) {
        this.$refs.Dialog.showToast("danger", "ไม่สามารถโหลดข้อมูลได้");
      } finally {
        this.tableBusy = false;
        this.isLoading = false;
      }
    },
    openCorrectionModal(item) {
      this.$refs.dfCorrectionModal.show(item, this.doctorsList);
    },
    openHistoryModal(item) {
      this.$refs.dfCorrectionHistoryModal.show(item);
    },
    onCorrectionSaved() {
      this.fetchReport();
      this.$refs.Dialog.showToast("success", "บันทึกการแก้ไข DF สำเร็จ");
    },
    onError(msg) {
      this.$refs.Dialog.showToast("danger", msg);
    },
  },
};
</script>

<style scoped>
.df-correction-page {
  --bg: #f4f6f9;
  --card: #ffffff;
  --ink: #0f1b2d;
  --ink-2: #2a3950;
  --muted: #6b7a90;
  --muted-2: #8a96a8;
  --line: #e3e8ef;
  --line-2: #eef1f6;
  --primary: #2563eb;
  --primary-600: #1d4ed8;
  --primary-50: #eff4ff;
  --warning: #b45309;
  --warning-bg: #fef3c7;
  --warning-border: #fde68a;
  --chip-bg: #f1f4f9;
  --shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.04);
  color: var(--ink);
  padding: 22px 28px 64px;
}

/* Page header */
.page-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}
.page-head .crumbs {
  font-size: 12.5px;
  color: var(--muted);
  display: flex;
  gap: 6px;
  align-items: center;
  margin-bottom: 6px;
}
.page-head .crumbs i {
  font-size: 9px;
  opacity: 0.55;
}
.page-head .crumbs-current {
  color: var(--ink-2);
}
.page-head h1 {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 10px;
}
.page-head h1 .title-fa {
  color: var(--ink);
  font-size: 18px;
}
.page-head .lede {
  color: var(--muted);
  font-size: 13px;
  margin-top: 4px;
}
.page-head .head-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  background: var(--chip-bg);
  color: var(--ink-2);
  font-size: 12px;
  border: 1px solid var(--line);
  white-space: nowrap;
}
.pill i {
  font-size: 10px;
  color: var(--muted);
}
.pill i.status-ok {
  color: #10b981;
}

/* Card */
.df-card {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 10px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

/* Filter bar */
.filter-bar {
  padding: 14px 16px;
  border-bottom: 1px solid var(--line);
  background: linear-gradient(180deg, #fafbfd, #ffffff);
}
.filter-grid {
  display: grid;
  grid-template-columns: 1.4fr 1.1fr 1.4fr auto;
  gap: 10px 14px;
  align-items: end;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}
.field label {
  font-size: 11.5px;
  font-weight: 500;
  color: var(--muted);
  letter-spacing: 0.01em;
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
}
.field label i {
  font-size: 10px;
  opacity: 0.7;
}
.ctrl {
  height: 36px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  padding: 0 12px;
  font: inherit;
  font-size: 13.5px;
  color: var(--ink);
  outline: none;
  transition: border-color 0.12s, box-shadow 0.12s;
  width: 100%;
}
.ctrl::placeholder {
  color: var(--muted-2);
}
.ctrl:hover {
  border-color: #cdd5e0;
}
.ctrl:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}
.ctrl-wrap {
  position: relative;
}
.ctrl-wrap > i.lead {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted);
  font-size: 12px;
  pointer-events: none;
  z-index: 1;
}
.ctrl-wrap > i.lead + .ctrl {
  padding-left: 32px;
}
.ctrl-wrap > .trail {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted);
  font-size: 12px;
}
.ctrl-wrap > .trail.clearable {
  cursor: pointer;
  pointer-events: auto;
}
.select-wrap::after {
  content: "\f078";
  font-family: "Font Awesome 5 Free";
  font-weight: 900;
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted);
  font-size: 10px;
  pointer-events: none;
}
select.ctrl {
  appearance: none;
  -webkit-appearance: none;
  background: #fff;
  padding-right: 32px;
}

/* Date range wrap (around vue2-datepicker) */
.date-range-wrap {
  position: relative;
  display: flex;
  align-items: center;
  height: 36px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  padding: 0 8px 0 0;
}
.date-range-wrap:focus-within {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}
.date-range-wrap.is-invalid {
  border-color: #fca5a5;
}
.date-range-wrap >>> .mx-datepicker,
.date-range-wrap >>> .mx-datepicker-range {
  width: 100%;
  flex: 1;
}
.date-range-wrap >>> .mx-input-wrapper {
  border: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
}
.date-range-wrap >>> .df-date-input,
.date-range-wrap >>> .df-date-input:hover,
.date-range-wrap >>> .df-date-input:focus {
  border: 0 !important;
  box-shadow: none !important;
  outline: none !important;
  background: transparent !important;
  border-radius: 0 !important;
  height: 34px;
  font-size: 13.5px;
  color: var(--ink);
  padding-left: 12px;
  width: 100%;
}
.date-range-wrap >>> .mx-input-wrapper .mx-icon-calendar,
.date-range-wrap >>> .mx-input-wrapper .mx-icon-clear {
  color: var(--muted);
}
.date-quick {
  font-size: 11.5px;
  color: var(--primary);
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  background: var(--primary-50);
  white-space: nowrap;
  margin-left: 4px;
}
.date-quick:hover {
  background: #e0eaff;
}

/* Buttons */
.btn {
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font: inherit;
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  transition: background 0.12s, border-color 0.12s, color 0.12s,
    box-shadow 0.12s;
  white-space: nowrap;
}
.btn-primary {
  background: var(--primary);
  color: #fff;
  box-shadow: 0 1px 2px rgba(37, 99, 235, 0.25);
}
.btn-primary:hover {
  background: var(--primary-600);
}
.btn-primary:active {
  transform: translateY(1px);
}
.btn-secondary {
  background: #fff;
  color: var(--ink-2);
  border-color: var(--line);
}
.btn-secondary:hover {
  background: var(--line-2);
  border-color: #cdd5e0;
}
.btn-xs {
  height: 28px;
  padding: 0 10px;
  font-size: 12.5px;
  border-radius: 6px;
}
.btn-icon {
  width: 28px;
  padding: 0;
  justify-content: center;
}
.btn-outline {
  background: #fff;
  color: var(--primary);
  border-color: #c7d6f5;
}
.btn-outline:hover {
  background: var(--primary-50);
}
.btn-outline:disabled,
.btn-outline:disabled:hover {
  background: #f4f6f9;
  color: var(--muted-2);
  border-color: var(--line);
  cursor: not-allowed;
  opacity: 0.7;
}
.btn-outline-info {
  background: #fff;
  color: #0e7490;
  border-color: #bae6fd;
  margin-left: 6px;
}
.btn-outline-info:hover {
  background: #f0f9ff;
}
.field.actions {
  flex-direction: row;
  gap: 8px;
  align-items: end;
}

/* Quick chips row */
.quick-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--line);
  background: #fff;
  flex-wrap: wrap;
}
.quick-row .label-text {
  font-size: 11.5px;
  color: var(--muted);
  margin-right: 4px;
}
.chip {
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: #fff;
  font-size: 12px;
  color: var(--ink-2);
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  user-select: none;
}
.chip:hover {
  background: var(--line-2);
}
.chip.active {
  background: var(--primary-50);
  color: var(--primary-600);
  border-color: #b9cef9;
}
.chip-count {
  color: var(--muted);
  font-weight: 500;
}
.chip.active .chip-count {
  color: var(--primary-600);
}
.chip-counts {
  margin-left: auto;
  display: flex;
  gap: 14px;
  font-size: 12px;
  color: var(--muted);
  flex-wrap: wrap;
}
.chip-counts strong {
  color: var(--ink);
  font-weight: 600;
}

/* Table toolbar */
.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid var(--line);
  gap: 12px;
}
.table-toolbar .left {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: var(--muted);
}
.table-toolbar .left strong {
  color: var(--ink);
  font-weight: 600;
}

/* Table */
.tbl-wrap {
  overflow: hidden;
}
.df-tbl >>> table.b-table {
  margin: 0;
  font-size: 13px;
}
.df-tbl >>> thead th {
  background: #f7f9fc !important;
  color: var(--muted);
  font-weight: 500;
  font-size: 11.5px;
  text-align: left;
  padding: 9px 12px;
  border-bottom: 1px solid var(--line);
  border-top: 0;
  white-space: nowrap;
  letter-spacing: 0.03em;
}
.df-tbl >>> tbody td {
  padding: 11px 12px;
  border-bottom: 1px solid var(--line-2);
  border-top: 0;
  color: var(--ink-2);
  vertical-align: middle;
}
.df-tbl >>> tbody tr:hover td {
  background: #fafbfd;
}
.df-tbl >>> td.col-idx,
.df-tbl >>> th.col-idx {
  color: var(--muted-2);
  width: 36px;
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.mono {
  font-family: "IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, Consolas,
    monospace;
  font-size: 12px;
}
.name {
  color: var(--ink);
  font-weight: 500;
}
.num {
  font-variant-numeric: tabular-nums;
}
.df-num {
  color: var(--ink);
  font-weight: 500;
}
.doctor-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}
.avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--primary-50);
  color: var(--primary);
  display: grid;
  place-items: center;
  font-size: 10.5px;
  font-weight: 600;
  flex-shrink: 0;
}

/* Status badges */
.badge-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 500;
  border: 1px solid transparent;
}
.badge-warn {
  background: var(--warning-bg);
  color: var(--warning);
  border-color: var(--warning-border);
  cursor: pointer;
}
.badge-warn:hover {
  filter: brightness(0.97);
}
.badge-ok {
  background: #f3f4f6;
  color: #4b5563;
  border-color: #e5e7eb;
}
.badge-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}
.badge-warn .badge-dot {
  background: #d97706;
}
.badge-ok .badge-dot {
  background: #9ca3af;
}

/* Empty */
.empty {
  padding: 56px 24px;
  text-align: center;
}
.empty .ic {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--line-2);
  color: var(--muted);
  display: grid;
  place-items: center;
  margin: 0 auto 12px;
  font-size: 16px;
}
.empty h4 {
  margin: 0 0 4px;
  font-size: 14px;
  color: var(--ink);
  font-weight: 600;
}
.empty p {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
}

/* Footer */
.tbl-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-top: 1px solid var(--line);
  font-size: 12.5px;
  color: var(--muted);
}
.df-pager >>> .page-link {
  border-radius: 6px !important;
  margin: 0 2px;
  border-color: var(--line);
  color: var(--ink-2);
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.df-pager >>> .page-item.active .page-link {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
}
.df-pager >>> .page-item.disabled .page-link {
  color: var(--muted-2);
}

/* Info strip */
.info-strip {
  margin-top: 16px;
  font-size: 12.5px;
  color: var(--muted);
  display: flex;
  gap: 10px;
  align-items: center;
}

/* Responsive */
@media (max-width: 1100px) {
  .filter-grid {
    grid-template-columns: 1fr 1fr;
  }
  .filter-grid > .actions {
    grid-column: 1 / -1;
    justify-self: end;
  }
}
@media (max-width: 720px) {
  .df-correction-page {
    padding: 14px 14px 40px;
  }
  .filter-grid {
    grid-template-columns: 1fr;
  }
  .page-head {
    flex-direction: column;
    align-items: flex-start;
  }
  .chip-counts {
    width: 100%;
    margin-left: 0;
  }
}
</style>
